import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { viewSitesForUser } from '../../../../services/StaffMemberService';
import { formatSites } from '../../../../util/SiteFormatUtil';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { CancelConfirmationModal } from '../../../common/cancel-confirmation-modal/CancelConfirmationModal';

import './StudyTrialsSitesTab.css';

export const StudyTrialsSitesTab = ({
  context,
  sitesFromBreadcrumbs,
  setContext,
  navigateTo,
  activeTab,
  setActiveTab,
  setShowStaffTab,
  handleSaveClick,
  toolTipsEnabled,
  setSpinnerText,
  showToaster,
}) => {
  const [sites, setSites] = useState([]);
  const [activeChildTab, setActiveChildTab] = useState('Site 1');
  const [siteFacilityNames, setSiteFacilityNames] = useState([]);
  const [tabSelections, setTabSelections] = useState({});
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [startDateMap, setStartDateMap] = useState({});
  const [accrualFromMap, setAccrualFromMap] = useState({});
  const [endDateMap, setEndDateMap] = useState({});
  const [accrualToMap, setAccrualToMap] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [provinceError, setProvinceError] = useState('');
  const [districtError, setDistrictError] = useState('');
  const [subDistrictError, setSubDistrictError] = useState('');
  const [facilityError, setFacilityError] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [accrualFromDateError, setAccrualFromDateError] = useState('');
  const [accrualToDateError, setAccrualToDateError] = useState('');

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    setActiveTab(activeTab);
    populateDropdowns();
    if (
      context.breadcrumbs.newStudySitesBreadcrumb?.data &&
      context.breadcrumbs.newStudySitesBreadcrumb?.data.length !== 0
    ) {
      const startDateMap = {};
      const accrualFromDateMap = {};
      const endDateMap = {};
      const accrualToDateMap = {};

      const updatedSites = context.breadcrumbs.newStudySitesBreadcrumb.data.map((site) => {
        const { name, province, district, subDistrict, facility, startDate, accrualFrom, endDate, accrualTo } = site;
        const selections = {
          selectedProvince: province,
          selectedDistrict: district,
          selectedSubDistrict: subDistrict,
          selectedFacility: { value: facility, label: facility.facility },
        };

        updateTabSelections(name, selections);

        startDateMap[name] = new Date(startDate);
        accrualFromDateMap[name] = new Date(accrualFrom);
        endDateMap[name] = new Date(endDate);
        accrualToDateMap[name] = new Date(accrualTo);
        return { ...site, selections };
      });

      setSites(updatedSites);
      setStartDateMap(startDateMap);
      setAccrualFromMap(accrualFromDateMap);
      setEndDateMap(endDateMap);
      setAccrualToMap(accrualToDateMap);
    } else {
      setSites([
        {
          name: 'Site 1',
          province: null,
          district: null,
          subDistrict: null,
          facility: null,
          startDate: null,
          accrualFrom: null,
          endDate: null,
          accrualTo: null,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sitesFromBreadcrumbs]);

  const updateScreenWidth = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  });

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const populateDropdowns = async () => {
    setSpinnerText('Retrieving Facilities');
    try {
      const [responseData, error] = await viewSitesForUser(context.username, context.loggedInUser.externalReference);
      if (responseData) {
        if (responseData.responseStatus === 'Success') {
          const assignedSites = responseData.assignedSites;
          const provinces = formatSites(assignedSites);

          const formattedProvinces = provinces.map((province) => ({
            value: province,
            label: province.province,
          }));

          setProvinces([...formattedProvinces]);
        } else {
          showToaster(responseData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    } catch (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message || 'Error retrieving provinces',
      });
    }
    setSpinnerText(null);
  };

  const handleStartDateChange = (selectedDate) => {
    setStartDateMap((prevState) => ({
      ...prevState,
      [activeChildTab]: selectedDate,
    }));
  };

  const handleEndDateChange = (selectedDate) => {
    setEndDateMap((prevState) => ({
      ...prevState,
      [activeChildTab]: selectedDate,
    }));
  };

  const handleAccrualFromChange = (selectedDate) => {
    setAccrualFromMap((prevState) => ({
      ...prevState,
      [activeChildTab]: selectedDate,
    }));
  };

  const handleAccrualToChange = (selectedDate) => {
    setAccrualToMap((prevState) => ({
      ...prevState,
      [activeChildTab]: selectedDate,
    }));
  };

  const handleProvinceChange = (selectedProvince) => {
    const selectedProvinceData = selectedProvince.value;
    const districtOptions = selectedProvinceData.districts.map((district) => ({
      value: district,
      label: district.district,
    }));

    setDistricts(districtOptions);
    updateTabSelections(activeChildTab, {
      ...getTabSelections(activeChildTab),
      selectedDistrict: null,
      selectedSubDistrict: null,
      selectedFacility: null,
    });
  };

  const handleDistrictChange = (selectedDistrict) => {
    const selectedDistrictData = selectedDistrict.value;
    const subDistrictOptions = selectedDistrictData.subDistricts.map((subDistrict) => ({
      value: subDistrict,
      label: subDistrict.subDistrict,
    }));
    setSubDistricts(subDistrictOptions);
    updateTabSelections(activeChildTab, {
      ...getTabSelections(activeChildTab),
      selectedSubDistrict: null,
      selectedFacility: null,
    });
  };

  const handleSubDistrictChange = (selectedSubDistrict) => {
    const selectedSubDistrictData = selectedSubDistrict.value;
    const facilityOptions = selectedSubDistrictData.facilities.map((facility) => ({
      value: facility,
      label: facility.facility,
    }));
    setFacilities(facilityOptions);
    updateTabSelections(activeChildTab, {
      ...getTabSelections(activeChildTab),
      selectedFacility: null,
    });
  };

  const updateTabSelections = (tabName, selections) => {
    setTabSelections((prevSelections) => ({
      ...prevSelections,
      [tabName]: selections,
    }));
  };

  const removeTabSelections = (tabName) => {
    setTabSelections((prevTabSelections) => {
      const removedSelections = prevTabSelections[tabName];

      if (removedSelections && removedSelections.selectedFacility) {
        addSelectedFacility(removedSelections);
      }

      const { [tabName]: removedTab, ...restTabs } = prevTabSelections;
      return restTabs;
    });
  };

  const getTabSelections = (tabName) => {
    return tabSelections[tabName] || {};
  };

  const removeSelectedFacility = (selectedFacilities) => {
    if (!Array.isArray(selectedFacilities) || selectedFacilities.length === 0) {
      return;
    }

    const updatedProvinces = provinces.map((province) => {
      if (!province || !province.value || !Array.isArray(province.value.districts)) {
        return province;
      }

      const updatedDistricts = province.value.districts.map((district) => {
        if (!district || !Array.isArray(district.subDistricts)) {
          return district;
        }

        const updatedSubDistricts = district.subDistricts.map((subDistrict) => {
          if (!subDistrict || !Array.isArray(subDistrict.facilities)) {
            return subDistrict;
          }

          const updatedFacilities = subDistrict.facilities.filter((facility) => {
            if (!facility || !facility.facility || !facility.externalReference) {
              return false;
            }

            return !selectedFacilities.some(
              (selected) =>
                selected.value.facility === facility.facility &&
                selected.value.externalReference === facility.externalReference
            );
          });

          return { ...subDistrict, facilities: updatedFacilities };
        });

        return { ...district, subDistricts: updatedSubDistricts };
      });

      return { ...province, value: { ...province.value, districts: updatedDistricts } };
    });
    setProvinces(updatedProvinces);
  };

  const addSelectedFacility = (removedTabSelection) => {
    if (!removedTabSelection) {
      return;
    }

    setProvinces((prevProvinces) => {
      return prevProvinces.map((province) => {
        if (!province || !province.value || !Array.isArray(province.value.districts)) {
          return province;
        }

        return {
          ...province,
          value: {
            ...province.value,
            districts: province.value.districts.map((district) => {
              if (!district || !Array.isArray(district.subDistricts)) {
                return district;
              }

              return {
                ...district,
                subDistricts: district.subDistricts.map((subDistrict) => {
                  if (
                    !subDistrict ||
                    !Array.isArray(subDistrict.facilities) ||
                    subDistrict.subDistrict !== removedTabSelection.selectedSubDistrict.label ||
                    district.district !== removedTabSelection.selectedDistrict.label ||
                    province.value.province !== removedTabSelection.selectedProvince.label
                  ) {
                    return subDistrict;
                  }

                  const facilityExists = subDistrict.facilities.some(
                    (facility) =>
                      facility.facility === removedTabSelection.selectedFacility.label &&
                      facility.externalReference === removedTabSelection.selectedFacility.value.externalReference
                  );

                  if (!facilityExists) {
                    const updatedFacilities = [
                      ...subDistrict.facilities,
                      {
                        facility: removedTabSelection.selectedFacility.label,
                        externalReference: removedTabSelection.selectedFacility.value.externalReference,
                      },
                    ];
                    return { ...subDistrict, facilities: updatedFacilities };
                  }

                  return subDistrict;
                }),
              };
            }),
          },
        };
      });
    });
  };

  const clearValidationErrors = () => {
    setProvinceError('');
    setDistrictError('');
    setSubDistrictError('');
    setFacilityError('');
    setStartDateError('');
    setEndDateError('');
    setAccrualFromDateError('');
    setAccrualToDateError('');
  };

  const handleAddSiteClick = () => {
    const selectedFacility = sites.map((site) => {
      const tabSelections = getTabSelections(site.name);
      return tabSelections?.selectedFacility;
    });

    removeSelectedFacility(selectedFacility);

    if (sites.length >= 6) return;

    const newTabName = `Site ${sites.length + 1}`;
    setSites((prevSites) => [
      ...prevSites,
      {
        name: newTabName,
        province: null,
        district: null,
        subDistrict: null,
        facility: null,
        facilityExternalReference: null,
        startDate: null,
        accrualFrom: null,
        endDate: null,
        accrualTo: null,
      },
    ]);

    setActiveChildTab(newTabName);
    clearValidationErrors();
    setDistricts([]);
    setSubDistricts([]);
    setFacilities([]);
  };

  const handleSiteClick = (site) => {
    clearValidationErrors();

    setActiveChildTab(site.name);
    const selections = getTabSelections(site.name);

    updateTabSelections(site.name, { ...selections });

    if (selections.selectedProvince) {
      const province = selections.selectedProvince.value;
      const districtOptions = province.districts.map((district) => ({
        value: district,
        label: district.district,
      }));
      setDistricts(districtOptions);
    } else {
      setDistricts([]);
    }

    if (selections.selectedDistrict) {
      const district = selections.selectedDistrict.value;
      const subDistrictOptions = district.subDistricts.map((subDistrict) => ({
        value: subDistrict,
        label: subDistrict.subDistrict,
      }));
      setSubDistricts(subDistrictOptions);
    } else {
      setSubDistricts([]);
    }

    if (selections.selectedSubDistrict) {
      const subDistrict = selections.selectedSubDistrict.value;
      const facilityOptions = subDistrict.facilities.map((facility) => ({
        value: facility,
        label: facility.facility,
      }));
      setFacilities(facilityOptions);
    } else {
      setFacilities([]);
    }
  };

  const removeTab = (tabName, event) => {
    event.stopPropagation();
    if (sites.length > 1 && tabName !== 'Site 1') {
      const removedTabIndex = sites.findIndex((site) => site.name === tabName);
      let newActiveTab;
      if (removedTabIndex === 0) {
        newActiveTab = sites[1].name;
      } else {
        newActiveTab = sites[removedTabIndex - 1].name;
      }
      setSites((prevSites) => {
        const newSites = prevSites.filter((site) => site.name !== tabName);
        setActiveChildTab(newActiveTab);
        return newSites;
      });

      setSiteFacilityNames((prev) => {
        const updatedSiteFacilityName = [...prev];
        updatedSiteFacilityName.splice(removedTabIndex, 1);
        return updatedSiteFacilityName;
      });

      removeTabSelections(tabName);
    }
  };

  const validateRequiredFields = (site) => {
    let isValid = true;

    if (!site.province) {
      setProvinceError('*REQUIRED');
      isValid = false;
    } else {
      setProvinceError('');
    }

    if (!site.district) {
      setDistrictError('*REQUIRED');
      isValid = false;
    } else {
      setDistrictError('');
    }

    if (!site.subDistrict) {
      setSubDistrictError('*REQUIRED');
      isValid = false;
    } else {
      setSubDistrictError('');
    }

    if (!site.facility) {
      setFacilityError('*REQUIRED');
      isValid = false;
    } else {
      setFacilityError('');
    }

    if (!site.startDate) {
      setStartDateError('*REQUIRED');
      isValid = false;
    } else {
      setStartDateError('');
    }

    if (!site.endDate) {
      setEndDateError('*REQUIRED');
      isValid = false;
    } else {
      setEndDateError('');
    }

    if (!site.accrualTo) {
      setAccrualToDateError('*REQUIRED');
      isValid = false;
    } else {
      setAccrualToDateError('');
    }

    if (!site.accrualFrom) {
      setAccrualFromDateError('*REQUIRED');
      isValid = false;
    } else {
      setAccrualFromDateError('');
    }

    if (site.startDate && site.endDate) {
      if (site.endDate <= site.startDate) {
        setStartDateError('*End Date cannot be before Start Date');
        setEndDateError('*End Date cannot be before Start Date');
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSaveAndNextClick = () => {
    const updatedSites = sites.map((site) => {
      const tabSelections = getTabSelections(site.name);
      const selectedFacility = tabSelections?.selectedFacility;
      return {
        ...site,
        province: tabSelections?.selectedProvince,
        district: tabSelections?.selectedDistrict,
        subDistrict: tabSelections?.selectedSubDistrict,
        facility: selectedFacility?.value,
        facilityExternalReference: selectedFacility?.value?.externalReference,
        startDate: startDateMap[site.name],
        accrualFrom: accrualFromMap[site.name],
        endDate: endDateMap[site.name],
        accrualTo: accrualToMap[site.name],
      };
    });

    let isValidated;
    for (let i = 0; i < updatedSites.length; i++) {
      const updatedSite = updatedSites[i];
      isValidated = validateRequiredFields(updatedSite);

      if (!isValidated) {
        break;
      }
    }

    if (!isValidated) {
      return;
    }

    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudySitesBreadcrumb: {
          ...prevState.breadcrumbs.newStudySitesBreadcrumb,
          type: 'General',
          screenKey: 'studyTrialSites',
          data: updatedSites,
        },
      },
      editMode: true,
    }));
    handleSaveClick('studyTrialStaff');
    setShowStaffTab(true);
  };

  const handleCancelClick = () => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudySitesBreadcrumb: undefined,
        newStudyGeneralBreadcrumb: undefined,
        newStudyFormsBreadcrumb: undefined,
        newStudySchedulesBreadcrumb: undefined,
      },
      editMode: true,
    }));
    navigateTo('studyTrialAdmin');
  };

  const handleSetFacilityAndSiteNames = (selectedFacility) => {
    const index = parseInt(activeChildTab.charAt(activeChildTab.length - 1)) - 1;
    setSiteFacilityNames((prev) => {
      const updatedSiteFacilityName = [...prev];
      updatedSiteFacilityName[index] = selectedFacility.value.facility;
      return updatedSiteFacilityName;
    });
  };

  const renderEndDate = () => (
    <>
      <div className="input-container">
        <label className={'input-label-right'}>End Date:</label>
        <DatePicker
          selected={endDateMap[activeChildTab]}
          onChange={handleEndDateChange}
          className={`text-input ${endDateError && 'error-border'}`}
          dateFormat="dd/MM/yyy"
          showPopperArrow={false}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          placeholderText="Select..."
        />
        <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
        {toolTipsEnabled && (
          <ToolTip
            tooltipExternalReference={'siteEndDate123'}
            context={context}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            fieldName={'End Date:'}
          />
        )}
      </div>
      <div className="error-message">{endDateError && <div className="small-red-text">{endDateError}</div>}</div>
    </>
  );
  const siteValidationErrorCount = [provinceError, districtError, subDistrictError, facilityError].filter(
    Boolean
  ).length;

  const BASE_PADDING = 335;
  const ERROR_HEIGHT = 9;

  const paddingTop = BASE_PADDING + siteValidationErrorCount * ERROR_HEIGHT;

  return (
    <div className="study-trials-sites-tab-container">
      <div className="study-trials-sites-tab-container-content">
        <div className="study-trials-sites-tab-tab-container">
          {sites.map((site, index) => (
            <div
              key={site.name}
              className={`study-trials-sites-tab ${activeChildTab === site.name ? 'active-tab' : ''}`}
              onClick={() => handleSiteClick(site)}
            >
              {site.name !== 'Site 1' && (
                <div className="close-icon" onClick={(event) => removeTab(site.name, event)}>
                  &#x2716;
                </div>
              )}
              <div className="study-trials-sites-tab-text">{siteFacilityNames[index] || site.name}</div>
            </div>
          ))}
          {sites.length < 6 && (
            <div className="study-trials-sites-tab" onClick={handleAddSiteClick}>
              Add Site
            </div>
          )}
        </div>
        <div className={`study-trials-sites-tab-content ${activeTab}`}>
          <div className="site-tab-container" style={{ justifyContent: 'normal' }}>
            <div className="study-trials-sites-tab-inputs">
              <div className="form-section-left" style={{ paddingTop: '0' }}>
                <h3 className="sites-header">Site:</h3>
                <div className="input-container">
                  <label className={'input-label'}>Province:</label>
                  <Select
                    options={provinces}
                    closeMenuOnSelect={true}
                    onChange={(selectedOption) => {
                      handleProvinceChange(selectedOption, activeChildTab);
                      updateTabSelections(activeChildTab, {
                        ...getTabSelections(activeChildTab),
                        selectedProvince: selectedOption,
                      });
                    }}
                    value={getTabSelections(activeChildTab)?.selectedProvince || null}
                    classNamePrefix={`input-select${provinceError ? 'error-border' : ''}`}
                  />
                  {toolTipsEnabled && (
                    <ToolTip
                      tooltipExternalReference={'siteProvince123'}
                      context={context}
                      setSpinnerText={setSpinnerText}
                      showToaster={showToaster}
                      fieldName={'Province:'}
                    />
                  )}
                </div>
                <div className="error-message">
                  {provinceError && <div className="small-red-text">{provinceError}</div>}
                </div>
                <div className="input-container">
                  <label className={'input-label'}>District:</label>
                  <Select
                    options={districts}
                    closeMenuOnSelect={true}
                    onChange={(selectedOption) => {
                      handleDistrictChange(selectedOption, activeChildTab);
                      updateTabSelections(activeChildTab, {
                        ...getTabSelections(activeChildTab),
                        selectedDistrict: selectedOption,
                      });
                    }}
                    value={getTabSelections(activeChildTab)?.selectedDistrict || null}
                    classNamePrefix={`input-select${districtError ? 'error-border' : ''}`}
                  />
                  {toolTipsEnabled && (
                    <ToolTip
                      tooltipExternalReference={'siteDistrict123'}
                      context={context}
                      setSpinnerText={setSpinnerText}
                      showToaster={showToaster}
                      fieldName={'District:'}
                    />
                  )}
                </div>
                <div className="error-message">
                  {districtError && <div className="small-red-text">{districtError}</div>}
                </div>
                <div className="input-container">
                  <label className={'input-label'}>Sub-District:</label>
                  <Select
                    options={subDistricts}
                    closeMenuOnSelect={true}
                    onChange={(selectedOption) => {
                      handleSubDistrictChange(selectedOption, activeChildTab);
                      updateTabSelections(activeChildTab, {
                        ...getTabSelections(activeChildTab),
                        selectedSubDistrict: selectedOption,
                      });
                    }}
                    value={getTabSelections(activeChildTab)?.selectedSubDistrict || null}
                    classNamePrefix={`input-select${subDistrictError ? 'error-border' : ''}`}
                  />
                  {toolTipsEnabled && (
                    <ToolTip
                      tooltipExternalReference={'siteSubDistrict123'}
                      context={context}
                      setSpinnerText={setSpinnerText}
                      showToaster={showToaster}
                      fieldName={'Sub-District:'}
                    />
                  )}
                </div>
                <div className="error-message">
                  {subDistrictError && <div className="small-red-text">{subDistrictError}</div>}
                </div>
                <div className="input-container">
                  <label className={'input-label'}>Facility:</label>
                  <Select
                    options={facilities}
                    closeMenuOnSelect={true}
                    onChange={(selectedOption) => {
                      updateTabSelections(activeChildTab, {
                        ...getTabSelections(activeChildTab),
                        selectedFacility: selectedOption,
                      });
                      handleSetFacilityAndSiteNames(selectedOption);
                    }}
                    value={getTabSelections(activeChildTab)?.selectedFacility || null}
                    classNamePrefix={`input-select${facilityError ? 'error-border' : ''}`}
                  />
                  {toolTipsEnabled && (
                    <ToolTip
                      tooltipExternalReference={'siteFacilities123'}
                      context={context}
                      setSpinnerText={setSpinnerText}
                      showToaster={showToaster}
                      fieldName={'Facility:'}
                    />
                  )}
                </div>
                <div className="error-message">
                  {facilityError && <div className="small-red-text">{facilityError}</div>}
                </div>
                <h3 className="sites-header">Dates:</h3>
                <div className="input-container">
                  <label className={'input-label'}>Site Activation Date:</label>
                  <DatePicker
                    selected={startDateMap[activeChildTab]}
                    className={`text-input ${startDateError && 'error-border'}`}
                    dateFormat="dd/MM/yyy"
                    showPopperArrow={false}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    placeholderText="Select..."
                    onChange={handleStartDateChange}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                  {toolTipsEnabled && (
                    <ToolTip
                      tooltipExternalReference={'siteStartDate123'}
                      context={context}
                      setSpinnerText={setSpinnerText}
                      showToaster={showToaster}
                      fieldName={'Site Activation Date:'}
                    />
                  )}
                </div>
                <div className="error-message">
                  {startDateError && <div className="small-red-text">{startDateError}</div>}
                </div>

                {screenWidth <= 1560 && renderEndDate()}

                <div className="input-container">
                  <label className={'input-label'}>Accrual Period From:</label>
                  <DatePicker
                    selected={accrualFromMap[activeChildTab]}
                    onChange={handleAccrualFromChange}
                    className={`text-input ${accrualFromDateError && 'error-border'}`}
                    dateFormat="dd/MM/yyy"
                    showPopperArrow={false}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    placeholderText="Select..."
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                  {toolTipsEnabled && (
                    <ToolTip
                      tooltipExternalReference={'siteAccrualFrom123'}
                      context={context}
                      setSpinnerText={setSpinnerText}
                      showToaster={showToaster}
                      fieldName={'Accrual Period From:'}
                    />
                  )}
                </div>
                <div className="error-message">
                  {accrualFromDateError && <div className="small-red-text">{accrualFromDateError}</div>}
                </div>
              </div>

              <div className="form-section-right" style={{ paddingTop: '0' }}>
                <div
                  className="site-tab-lower-right-container"
                  style={{
                    paddingTop: `${paddingTop}px`,
                  }}
                >
                  {screenWidth > 1560 && renderEndDate()}

                  <div className="input-container">
                    <label className={'input-label-right'}>Accrual Period To:</label>
                    <DatePicker
                      selected={accrualToMap[activeChildTab]}
                      onChange={handleAccrualToChange}
                      className={`text-input ${accrualToDateError && 'error-border'}`}
                      dateFormat="dd/MM/yyy"
                      showPopperArrow={false}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      placeholderText="Select..."
                    />
                    <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
                    {toolTipsEnabled && (
                      <ToolTip
                        tooltipExternalReference={'siteAccrualTo123'}
                        context={context}
                        setSpinnerText={setSpinnerText}
                        showToaster={showToaster}
                        fieldName={'Accrual Period To:'}
                      />
                    )}
                  </div>
                  <div className="error-message">
                    {accrualToDateError && <div className="small-red-text">{accrualToDateError}</div>}
                  </div>
                </div>
              </div>
              {isCancelModalOpen && (
                <CancelConfirmationModal
                  closeCancelModal={closeCancelModal}
                  handleCancelClick={handleCancelClick}
                  setIsCancelModalOpen={setIsCancelModalOpen}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="study-button-container">
        <SaveButton
          color={'#ffffff'}
          border={'1px solid #db602e'}
          textColor={'#db602e'}
          title={'Cancel'}
          onClick={() => setIsCancelModalOpen(true)}
        />
        <SaveButton color={'#db602e'} title={'Save and Next'} onClick={handleSaveAndNextClick} />
      </div>
    </div>
  );
};
